import React, { Component, ReactNode } from 'react';
import ApplicationErrorFallback from './components/ApplicationErrorFallback';
import { useLocation } from 'react-router-dom';
interface ApplicationErrorBoundaryProps {
  children: ReactNode;
}

interface ApplicationErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  location: string;
}

class ApplicationErrorBoundaryClass extends Component<
  ApplicationErrorBoundaryProps & { location: string },
  ApplicationErrorBoundaryState
> {
  constructor(props: ApplicationErrorBoundaryProps & { location: string }) {
    super(props);
    this.state = { hasError: false, error: null, location: props.location };
  }

  static getDerivedStateFromError(error: Error): Partial<ApplicationErrorBoundaryState> {
    console.error('Error caught by application error boundary:', error);
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: ApplicationErrorBoundaryProps & { location: string },
    state: ApplicationErrorBoundaryState,
  ): Partial<ApplicationErrorBoundaryState> | null {
    if (props.location !== state.location && state.hasError) {
      return { hasError: false, error: null, location: props.location };
    }
    if (props.location !== state.location) {
      return { location: props.location };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by application error boundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ApplicationErrorFallback />;
    }

    return this.props.children;
  }
}

export const ApplicationErrorBoundary = ({ children }: ApplicationErrorBoundaryProps) => {
  const location = useLocation();
  return <ApplicationErrorBoundaryClass location={location.pathname}>{children}</ApplicationErrorBoundaryClass>;
};
